import { useMemo, useState } from 'react';
import { useTrial } from '../subscription/trial';
import { Link, useParams } from 'react-router-dom';
import { CONCEPTS, codeOf } from './registry';
import { explainCode } from './programCode';
import LibraryPlayground from './LibraryPlayground';
import { useLearner } from '../learners/LearnerContext';
import { addFromLibrary } from './library';
import { useRatings } from './useRatings';
import { useT } from '../i18n/i18n';

export default function LibraryPreviewPage() {
  const t = useT();
  const { trial, inTrial } = useTrial();
  const { id } = useParams();
  const concept = useMemo(() => CONCEPTS.find((c) => c.id === id), [id]);
  const { learnerId } = useLearner();
  const [busy, setBusy] = useState(false);
  const [addedId, setAddedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { difficultyOf } = useRatings();

  if (!concept) {
    return (
      <div className="card">
        <p>{t('This is not in the library.')}</p>
        <Link className="btn secondary" to="/library">{t('Back to the library')}</Link>
      </div>
    );
  }

  const item = concept;
  async function add() {
    setBusy(true);
    setError(null);
    try {
      setAddedId((await addFromLibrary(item, learnerId)).id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not add "{program}".', { program: item.label }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="banner" role="region" aria-label={t('Library item')}>
        <strong>{concept.label}</strong> <code className="library-code" title={explainCode(codeOf(concept.id), concept.label, concept.category, concept.kind)}>{codeOf(concept.id)}</code>
        <div className="muted" style={{ marginTop: 4 }}>{t(concept.description)}</div>
        {difficultyOf(concept.id) !== null && (
          <div className="field-hint" style={{ marginTop: 4 }}>
            {t('EasyLearning difficulty: {n} out of 10 — a guide only.', { n: difficultyOf(concept.id) ?? '' })}
          </div>
        )}
        <div className="toolbar" style={{ marginTop: 10 }}>
          {addedId && (
            <>
              <span className="library-added" role="status">✓ {t('Added')}</span>
              <Link to={`/programs/${addedId}/edit`} className="btn ghost">{t('Edit')}</Link>
            </>
          )}
          {trial?.limited && !inTrial(concept.id) ? (
            <Link to="/account/subscription" className="btn">{t('Start your full trial to add this')}</Link>
          ) : (
          <button type="button" className="btn" disabled={busy} onClick={() => void add()}>
            {busy ? t('Adding…') : addedId ? t('Add again') : t('Add to my programs')}
          </button>
          )}
          <Link to="/library" className="btn secondary">← {t('Back to the library')}</Link>
        </div>
        {error && <div className="error" role="alert">{error}</div>}
      </div>

      <LibraryPlayground concept={concept} />
    </div>
  );
}
